<template>
	<div class="tab-page">
		<form @submit.prevent="save" ref="form">
			<div class="field">
				<div class="container">
					<div class="content">
						<div class="field-inner">
							<label for>{{$t('composition')}}</label>
							<div class="element">
								<div class="flex">
									<div v-if="showCores" class="result-group">
										<input
											v-for="(core, index) in data.cores"
											:key="'core-' + index"
											type="text"
											:value="coreLabel(core)"
											readonly
										/>
									</div>
									<template v-else>
										<div class="result-group">
											<input
												v-for="(core, index) in data.coresA"
												:key="'core-' + index"
												type="text"
												:value="$t('cores-a') + ' ' + coreLabel(core)"
												readonly
											/>
										</div>
										<div class="result-group">
											<input
												v-for="(core, index) in data.coresB"
												:key="'core-' + index"
												type="text"
												:value="$t('cores-b') + ' ' + coreLabel(core)"
												readonly
											/>
										</div>
									</template>
									<div class="result-group">
										<input
											v-for="(covering, index) in data.coverings"
											:key="'covering-' + index"
											type="text"
											:value="coveringLabel(covering)"
											readonly
										/>
									</div>
									<div class="result-group">
										<input
											v-for="(effect, index) in data.effects"
											:key="'effect-' + index"
											type="text"
											:value="effectLabel(effect)"
											readonly
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="field">
				<div class="container">
					<div class="content">
						<div class="field-inner">
							<label for>{{$t('final-yarn-count')}}</label>
							<div class="element scroll-x">
								<YarnCountTable :units.sync="result.units" :selected="$settings.data.defaultYarnCount" />
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="field">
				<div class="container">
					<div class="content">
						<div class="field-inner">
							<label for="yarn-name">{{$t('yarn-name')}}*</label>
							<div class="element">
								<div class="flex">
									<input type="text" id="yarn-name" v-model="data.yarnName" placeholder=" " required />
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</form>
		<div class="field">
			<div class="container">
				<div class="content">
					<div class="field-inner">
						<label for="saving-mode">{{$t('saving-mode')}}</label>
						<div class="element">
							<div class="flex">
								<select id="saving-mode" v-model="savingMode">
									<option :value="0">{{$t('local-save')}}</option>
									<option :disabled="!isAuthenticated" :value="1">{{$t('cloud-save')}}</option>
								</select>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="container">
			<div class="content">
				<div class="field">
					<label for="overwrite"><input v-model="overwrite" id="overwrite" style="margin-left: 1rem; margin-right: 1rem;" type="checkbox"/>{{$t('overwrite')}} </label>
				</div>
			</div>
		</div>
		<div class="container">
			<div class="content">
				<button class @click="save">{{$t('save')}}</button>
			</div>
		</div>
	</div>
</template>

<script>
import YarnCountTable from '@/components/YarnCountTable.vue';

export default {
	props: ['data', 'result'],
	data() {
		return {
			overwrite: false,
			isAuthenticated: this.$store.getters.isAuthenticated,
			savingMode: !this.$store.getters.isAuthenticated ? 0 : 1,
		};
	},
	computed: {
		showCores() {
			let rst = true;
			if (this.data.workingPositions == 2) rst = false;
			return rst;
		}
	},
	components: {
		YarnCountTable
	},
	methods: {
		save() {
			if (!this.$refs.form.checkValidity()) {
				// Try focus on the error
				this.$refs.form.reportValidity();
				return;
			}
			switch (this.savingMode) {
				case 0:
					this.localSave();
					break;
				case 1:
					this.cloudSave();
					break;
			}
		},
		coreLabel(field) {
			if (this.data.workingPositions == 1) {
				return (
					this.$t('core') +
					' ' +
					this.$utils.sanitize(field.yarnCount.value) +
					' ' +
					this.$t(field.yarnCount.name) +
					' = ' +
					this.percentage(field.result.percentage)
				);
			}
			return (
				this.$utils.sanitize(field.yarnCount.value) +
				' ' +
				this.$t(field.yarnCount.name) +
				' = ' +
				this.percentage(field.result.percentage)
			);
		},
		coveringLabel(field) {
			return (
				this.$t('covering') +
				' ' +
				this.$utils.sanitize(field.yarnCount.value) +
				' ' +
				this.$t(field.yarnCount.name) +
				' = ' +
				this.percentage(field.result.percentage)
			);
		},
		effectLabel(field) {
			var rst = '';
			rst += this.$t('effect');

			switch (this.data.effectType) {
				case 'roving':
					rst +=
						' ' +
						this.$utils.sanitize(field.weight) +
						' ' +
						this.$t('gr');
					break;
				case 'yarn':
					rst +=
						' ' +
						this.$utils.sanitize(field.yarnCount.value) +
						' ' +
						this.$t(field.yarnCount.name);
					break;
			}
			rst += ' = ' + this.percentage(field.result.percentage);
			return rst;
		},
		percentage(value) {
			return (value * 100).toFixed(3) + '%';
		},
		localSave() {
			let self = this;

			if (!self.data.yarnName.length) {
				self.$snack.danger({
					button: null,
					text: self.$i18n.t('compile-all-required-fields')
				});
				return;
			}

			let yarn = {
				data: self.data,
				result: self.result
			};

			self.$storage
				.getItem('yarns-archive')
				.then(result => {
					let yarnsArchive = [];
					if (result) yarnsArchive = result;

					const index = yarnsArchive.findIndex(o => o.data.yarnName === yarn.data.yarnName);

					if (index >= 0 && !this.overwrite) {
						self.$snack.danger({
							button: null,
							text: self.$i18n.t('yarn-duplicate-name-error')
						});
						return Promise.reject();
					} else if(index >= 0 && this.overwrite){
						yarnsArchive[index] = {...yarn, id: yarnsArchive[index].id};
					} else {
						let id = 0;

						if (yarnsArchive.length) {
							id = Math.max(...yarnsArchive.map(o => o.id ));
						}

						while (yarnsArchive.find(o => o.id === id)) {
							id++;
						}

						yarn.id = id;
						yarn.timestamp = new Date().toISOString();
						yarnsArchive.push(yarn);
					}
					return self.$storage.setItem('yarns-archive', yarnsArchive);
				})
				.then(() => {
					self.$store.dispatch('updateYarnsArchive');
					self.$snack.success({
						button: null,
						text: self.$i18n.t('yarn-saved-successfully')
					});
				})
				.catch(() => {});
		},
		cloudSave() {
			if (this.isAuthenticated) {
				let params = new URLSearchParams();
				const resultData = {result: this.result, data: this.data};
				const stringifiedData = JSON.stringify(resultData);
				params.append('data', stringifiedData);
				const yarnName = this.data.yarnName;
				params.append('yarnName', yarnName);
				params.append('overwrite', this.overwrite);
				axios
					.post('/yarns', params)
					.then(() => {
						this.$store.dispatch('updateYarnsArchive');
						this.$snack.success({
							button: null,
							text: this.$i18n.t('yarn-saved-successfully')
						});
					})
					.catch((err) => {
						let resp = err.response;
						if (resp.data.response && resp.data.response.duplicate === 'yarn_name') {
							this.$snack.danger({
								button: null,
								text: this.$i18n.t('yarn-duplicate-name-error')
							});
						} else {
							this.$snack.danger({
								button: null,
								text: this.$i18n.t('creation-error')
							});
						}
						this.creationError = true;
						if (navigator.vibrate){
							navigator.vibrate([500]);
						}
					});
			}
		}
	}
};
</script>

<style scoped>
.result-group + .result-group {
	margin-top: 0.8rem;
}

.result-group > input + input {
	border-top: none;
}
</style>